import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Text } from "../components/ui/Text";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { PasswordInput } from "../components/ui/PasswordInput";
import { useAuth, User } from "../context/AuthContext";
import { useApi } from "../context/ApiContext";
import authApi from "../services/authService";

export const Profile: React.FC = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const { userInfo, user, loading: authLoading, refreshUserInfo } = useAuth();
 const { api } = useApi();

 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState(false);
 const [passwordLoading, setPasswordLoading] = useState(false);
 const [passwordError, setPasswordError] = useState<string | null>(null);
 const [passwordSuccess, setPasswordSuccess] = useState(false);
 const [verificationLoading, setVerificationLoading] = useState(false);
 const [verificationError, setVerificationError] = useState<string | null>(null);
 const [verificationSuccess, setVerificationSuccess] = useState(false);
 const [formData, setFormData] = useState({
  name: "",
  email: "",
  phone: "",
 });
 const [passwordData, setPasswordData] = useState({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
 });
 
 // Check if we have a token in localStorage
 const token = localStorage.getItem("token");
 const [localUser, setLocalUser] = useState<User | null>(null);

 // Fallback: If we have a token but user isn't set from AuthContext, decode it directly
 // This handles the case where AuthContext hasn't updated yet
 useEffect(() => {
  if (!user && token && !localUser) {
   try {
    if (token) {
     const decoded = jwtDecode<User>(token);
     setLocalUser(decoded);
    }
   } catch (error) {
    // Invalid token, will be handled by redirect logic
    console.error("Failed to decode token in Profile:", error);
   }
  }
 }, [user, token, localUser]);

 // Refresh userInfo when Profile page mounts to get latest verification status
 useEffect(() => {
  if (token && refreshUserInfo) {
   refreshUserInfo();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
  // Check if we have a token first - if not, redirect immediately
  if (!token) {
   navigate("/login", { replace: true });
   return undefined;
  }

  // Use effectiveUser (from AuthContext or local decode)
  const effectiveUser = user || localUser;

  // If we have a user (from token), set form data
  if (effectiveUser) {
   // We have user from token, set form data if userInfo is available
   if (userInfo) {
    setFormData({
     name: (userInfo as any)?.user?.name || effectiveUser.name || "",
     email: (userInfo as any)?.user?.email || effectiveUser.email || "",
     phone: (userInfo as any)?.user?.phone || "",
    });
   } else {
    // userInfo not loaded yet, but we have user from token, use that
    setFormData({
     name: effectiveUser.name || "",
     email: effectiveUser.email || "",
     phone: "",
    });
   }
   return undefined;
  } else if (!effectiveUser && !authLoading) {
   // No user and loading is complete, redirect to login
   // But give it a moment - user might be set synchronously from token
   const timeout = setTimeout(() => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
     navigate("/login", { replace: true });
    }
   }, 100);
   return () => clearTimeout(timeout);
  }
  return undefined;
 }, [userInfo, user, localUser, authLoading, navigate, token]);

 const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
   ...prev,
   [name]: value,
  }));
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);
  setError(null);
  setSuccess(false);

  try {
   await api.put("/users/profile", formData);
   setSuccess(true);
   setTimeout(() => setSuccess(false), 3000);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to update profile");
  } finally {
   setLoading(false);
  }
 };

 const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  setPasswordData((prev) => ({
   ...prev,
   [name]: value,
  }));
 };

 const handlePasswordSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setPasswordLoading(true);
  setPasswordError(null);
  setPasswordSuccess(false);

  // Validation
  if (passwordData.newPassword !== passwordData.confirmPassword) {
   setPasswordError(t("validation.passwords_mismatch"));
   setPasswordLoading(false);
   return;
  }

  if (passwordData.newPassword.length < 8) {
   setPasswordError("Password must be at least 8 characters long");
   setPasswordLoading(false);
   return;
  }

  if (!/\d/.test(passwordData.newPassword)) {
   setPasswordError("Password must contain at least one number");
   setPasswordLoading(false);
   return;
  }

  if (!/[a-zA-Z]/.test(passwordData.newPassword)) {
   setPasswordError("Password must contain at least one letter");
   setPasswordLoading(false);
   return;
  }

  try {
   await authApi.changePassword(passwordData.currentPassword, passwordData.newPassword);
   setPasswordSuccess(true);
   setPasswordData({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
   });
   setTimeout(() => setPasswordSuccess(false), 3000);
  } catch (err: any) {
   setPasswordError(
    err?.response?.data?.error ||
     err?.response?.data?.errors?.[0]?.msg ||
     "Failed to change password"
   );
  } finally {
   setPasswordLoading(false);
  }
 };

 const handleResendVerification = async () => {
  setVerificationLoading(true);
  setVerificationError(null);
  setVerificationSuccess(false);

  try {
   await authApi.resendVerificationEmail();
   setVerificationSuccess(true);
   setTimeout(() => setVerificationSuccess(false), 5000);
   // Refresh userInfo to get latest verification status
   await refreshUserInfo();
  } catch (err: any) {
   const errorMessage = err?.response?.data?.error || "Failed to send verification email";
   
   // If email is already verified, refresh userInfo and show success message
   if (errorMessage.includes("already verified") || errorMessage.includes("Email is already verified")) {
    await refreshUserInfo();
    setVerificationSuccess(true);
    setVerificationError(null);
    setTimeout(() => setVerificationSuccess(false), 5000);
   } else {
    setVerificationError(errorMessage);
   }
  } finally {
   setVerificationLoading(false);
  }
 };

 // Use user from AuthContext if available, otherwise use localUser (from token)
 const effectiveUser = user || localUser;

 // Get email verification status from userInfo
 const isEmailVerified = (userInfo as any)?.user?.isVerified ?? false;

 // Clear success message when email becomes verified
 useEffect(() => {
  if (isEmailVerified && verificationSuccess) {
   setVerificationSuccess(false);
  }
 }, [isEmailVerified, verificationSuccess]);
 
 // Show loading spinner only if we don't have a user (from either source)
 if (!effectiveUser) {
  if (!token) {
   // No token, show spinner (will redirect in useEffect)
   return (
    <Container>
     <Stack align="center" spacing="lg">
      <Spinner size="lg" />
     </Stack>
    </Container>
   );
  }
  
  // We have a token but user isn't set yet (from either AuthContext or local decode)
  // This should be very brief since token decoding is synchronous
  // But give it a moment for React state to update
  if (authLoading) {
   // Still loading, show spinner briefly
   return (
    <Container>
     <Stack align="center" spacing="lg">
      <Spinner size="lg" />
     </Stack>
    </Container>
   );
  }
  
  // Loading complete but no user - this shouldn't happen if token is valid
  // But if it does, show spinner (will redirect in useEffect)
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("menu.profile")}</Heading>

    {error && <Alert variant="error">{error}</Alert>}
    {success && <Alert variant="success">Profile updated successfully!</Alert>}

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
     <div className="lg:col-span-2 space-y-6">
      <Card padding="lg">
       <Stack spacing="md">
        <Heading size="lg">Personal Information</Heading>

        <form onSubmit={handleSubmit}>
         <Stack spacing="md">
          <div>
           <label className="block text-sm font-medium text-store-primary mb-2">
            Full Name
           </label>
           <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-store-accent-light focus:outline-none focus:border-store-accent focus:ring-2 focus:ring-store-accent focus:ring-opacity-30"
           />
          </div>

          <div>
           <label className="block text-sm font-medium text-store-primary mb-2">
            Email
           </label>
           <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled
            className="w-full px-4 py-2 border border-store-accent-light bg-gray-50 text-gray-500 cursor-not-allowed"
           />
           <div className="mt-1 flex items-center gap-2">
            <Text size="sm" color="muted">
             Email cannot be changed
            </Text>
            {isEmailVerified ? (
             <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
              ✓ Verified
             </span>
            ) : (
             <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              ⚠ Not Verified
             </span>
            )}
           </div>
          </div>

          <div>
           <label className="block text-sm font-medium text-store-primary mb-2">
            Phone
           </label>
           <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-store-accent-light focus:outline-none focus:border-store-accent focus:ring-2 focus:ring-store-accent focus:ring-opacity-30"
           />
          </div>

          <Button type="submit" loading={loading}>
           Save Changes
          </Button>
         </Stack>
        </form>
       </Stack>
      </Card>

      <Card padding="lg">
       <Stack spacing="md">
        <Heading size="lg">Change Password</Heading>

        {passwordError && <Alert variant="error">{passwordError}</Alert>}
        {passwordSuccess && (
         <Alert variant="success">{t("common.labels.passwordChangedSuccess")}</Alert>
        )}

        <form onSubmit={handlePasswordSubmit}>
         <Stack spacing="md">
          <PasswordInput
           label={t("common.labels.currentPassword")}
           name="currentPassword"
           value={passwordData.currentPassword}
           onChange={handlePasswordChange}
           placeholder={t("common.placeholders.enterCurrentPassword")}
           required
           className="w-full"
          />

          <PasswordInput
           label={t("common.labels.newPassword")}
           name="newPassword"
           value={passwordData.newPassword}
           onChange={handlePasswordChange}
           placeholder={t("common.placeholders.enterNewPassword")}
           helperText={t("validation.password_min_length", { length: 8 })}
           required
           className="w-full"
          />

          <PasswordInput
           label={t("common.labels.confirmNewPassword")}
           name="confirmPassword"
           value={passwordData.confirmPassword}
           onChange={handlePasswordChange}
           placeholder={t("common.placeholders.confirmNewPassword")}
           required
           className="w-full"
          />

          <Button type="submit" loading={passwordLoading}>
           {t("common.labels.changePassword")}
          </Button>
         </Stack>
        </form>
       </Stack>
      </Card>
     </div>

     <div>
      <Card padding="lg">
       <Stack spacing="md">
        <Heading size="lg">Account Info</Heading>

        <div>
         <Text size="sm" color="muted">
          Member Since
         </Text>
         <Text weight="semibold">
          {(userInfo as any)?.user?.createdAt
           ? new Date((userInfo as any).user.createdAt).toLocaleDateString()
           : "N/A"}
         </Text>
        </div>

        <div>
         <Text size="sm" color="muted">
          Account Status
         </Text>
         <Text weight="semibold" className="text-store-accent">
          Active
         </Text>
        </div>

        <div>
         <Text size="sm" color="muted" className="mb-2">
          Email Verification
         </Text>
         {isEmailVerified ? (
          <div>
           <Text weight="semibold" className="text-green-600 mb-2">
            ✓ Verified
           </Text>
           <Text size="sm" color="muted">
            Your email address has been verified
           </Text>
          </div>
         ) : (
          <div>
           <Text weight="semibold" className="text-yellow-600 mb-2">
            ⚠ Not Verified
           </Text>
           <Text size="sm" color="muted" className="mb-3">
            Please verify your email address to access all features
           </Text>
           {verificationError && (
            <Alert variant="error" className="mb-2">
             {verificationError}
            </Alert>
           )}
           {verificationSuccess && (
            <Alert variant="success" className="mb-2">
             {isEmailVerified 
              ? "Your email is verified!" 
              : "Verification email sent! Please check your inbox."}
            </Alert>
           )}
           <Button
            onClick={handleResendVerification}
            loading={verificationLoading}
            variant="outline"
            size="sm"
           >
            Send Verification Email
           </Button>
          </div>
         )}
        </div>

        {/* GDPR/Privacy Section */}
        <div className="pt-4 border-t border-store-secondary">
         <Text size="sm" color="muted" className="mb-2">
          {t("gdpr.privacyAndData")}
         </Text>
         <Text size="sm" color="muted" className="mb-3">
          {t("gdpr.privacyDescription")}
         </Text>
         <Link to="/gdpr">
          <Button variant="outline" size="sm" className="w-full">
            {t("gdpr.privacySettings")}
          </Button>
         </Link>
        </div>

       </Stack>
      </Card>
     </div>
    </div>
   </Stack>
  </Container>
 );
};

export default Profile;
